import React, { useEffect, useState } from 'react';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import {
  CreditCardIcon,
  PlusIcon,
  TrashIcon,
  CheckBadgeIcon,
  ShieldCheckIcon,
  DevicePhoneMobileIcon,
  StarIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';

const STORAGE_KEY = 'savedPaymentMethods';

const cardBrand = (number) => {
  const n = (number || '').replace(/\s/g, '');
  if (/^4/.test(n)) return 'Visa';
  if (/^5[1-5]/.test(n)) return 'Mastercard';
  if (/^3[47]/.test(n)) return 'Amex';
  if (/^6/.test(n)) return 'Discover';
  return 'Card';
};

const PaymentMethods = () => {
  const [activeTab, setActiveTab] = useState('saved');
  const [methods, setMethods] = useState([]);
  const [adding, setAdding] = useState(false);
  const [methodType, setMethodType] = useState('card');

  const [cardForm, setCardForm] = useState({
    cardNumber: '',
    holderName: '',
    expiry: '',
    cvv: '',
    nickname: '',
  });
  const [mobileForm, setMobileForm] = useState({
    provider: 'MTN',
    phone: '',
    holderName: '',
    nickname: '',
  });
  const [cardErrors, setCardErrors] = useState({});
  const [mobileErrors, setMobileErrors] = useState({});

  useEffect(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]');
      setMethods(saved);
    } catch { /* ignore */ }
  }, []);

  const persist = (next) => {
    setMethods(next);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
  };

  const handleAddCard = (e) => {
    e?.preventDefault?.();
    const digits = cardForm.cardNumber.replace(/\s/g, '');
    const nextErrors = {};
    if (!/^\d{13,19}$/.test(digits)) {
      nextErrors.cardNumber = 'Enter a valid card number with 13 to 19 digits';
    }
    if (!cardForm.holderName.trim()) {
      nextErrors.holderName = 'Cardholder name is required';
    }
    if (!/^\d{2}\/\d{2}$/.test(cardForm.expiry)) {
      nextErrors.expiry = 'Use MM/YY format';
    }
    if (!/^\d{3,4}$/.test(cardForm.cvv)) {
      nextErrors.cvv = 'CVV must be 3 or 4 digits';
    }
    setCardErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setAdding(true);
    try {
      const last4 = digits.slice(-4);
      const newMethod = {
        id: `pm_${Date.now().toString(36)}`,
        type: 'card',
        brand: cardBrand(digits),
        last4,
        holderName: cardForm.holderName.trim(),
        expiry: cardForm.expiry,
        nickname: cardForm.nickname.trim(),
        isDefault: methods.length === 0,
        createdAt: new Date().toISOString(),
      };
      persist([...methods, newMethod]);
      setCardForm({ cardNumber: '', holderName: '', expiry: '', cvv: '', nickname: '' });
      setCardErrors({});
      toast.success(`${newMethod.brand} •••• ${last4} added`);
      setActiveTab('saved');
    } catch (error) {
      console.error('Add card error:', error);
      toast.error('Failed to add card');
    } finally {
      setAdding(false);
    }
  };

  const handleAddMobile = (e) => {
    e?.preventDefault?.();
    const digits = mobileForm.phone.replace(/\D/g, '');
    const nextErrors = {};
    if (digits.length < 9) {
      nextErrors.phone = 'Enter a valid phone number';
    }
    if (!mobileForm.holderName.trim()) {
      nextErrors.holderName = 'Account holder name is required';
    }
    setMobileErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) {
      return;
    }

    setAdding(true);
    try {
      const masked = digits.slice(-4);
      const newMethod = {
        id: `pm_${Date.now().toString(36)}`,
        type: 'mobile',
        provider: mobileForm.provider,
        phone: digits,
        last4: masked,
        holderName: mobileForm.holderName.trim(),
        nickname: mobileForm.nickname.trim(),
        isDefault: methods.length === 0,
        createdAt: new Date().toISOString(),
      };
      persist([...methods, newMethod]);
      setMobileForm({ provider: 'MTN', phone: '', holderName: '', nickname: '' });
      setMobileErrors({});
      toast.success(`${newMethod.provider} •••• ${masked} added`);
      setActiveTab('saved');
    } catch (error) {
      console.error('Add mobile error:', error);
      toast.error('Failed to add mobile money');
    } finally {
      setAdding(false);
    }
  };

  const handleSetDefault = (id) => {
    persist(methods.map(m => ({ ...m, isDefault: m.id === id })));
    toast.success('Default payment method updated');
  };

  const handleDelete = (id) => {
    const m = methods.find(x => x.id === id);
    if (!window.confirm(`Remove ${m?.nickname || m?.brand || m?.provider} •••• ${m?.last4}?`)) return;
    const next = methods.filter(x => x.id !== id);
    if (m?.isDefault && next.length > 0) next[0].isDefault = true;
    persist(next);
    toast.success('Payment method removed');
  };

  const formatCardNumber = (raw) => raw.replace(/\D/g, '').replace(/(\d{4})(?=\d)/g, '$1 ').slice(0, 23);
  const formatExpiry = (raw) => {
    const d = raw.replace(/\D/g, '').slice(0, 4);
    return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
  };

  const tabs = [
    { id: 'saved', label: 'Saved Methods', icon: CreditCardIcon },
    { id: 'add', label: 'Add New', icon: PlusIcon },
  ];

  const updateCardField = (field, value) => {
    setCardForm(f => ({ ...f, [field]: value }));
    setCardErrors(e => ({ ...e, [field]: '' }));
  };

  const updateMobileField = (field, value) => {
    setMobileForm(f => ({ ...f, [field]: value }));
    setMobileErrors(e => ({ ...e, [field]: '' }));
  };


  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-xl sm:text-2xl mb-2" style={{ color: 'var(--gray-12)' }}>Payment Methods</h1>
          <p style={{ color: 'var(--gray-11)', textAlign: 'center' }}>
            Manage your saved cards and mobile money for faster checkout
          </p>
        </div>

        {/* Banner */}
        <div
          className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
          style={{ backgroundColor: 'var(--orange-2)', border: '1px solid var(--orange-5)' }}
        >
          <ShieldCheckIcon style={{ width: '20px', height: '20px', color: 'var(--orange-9)' }} />
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)' }}>
            Your payment data is encrypted. We never store full card numbers or CVV.
          </span>
        </div>

        {/* Tabs */}
        <div
          className="flex gap-1 mb-8 overflow-x-auto"
          style={{ borderBottom: '1px solid var(--gray-4)', paddingBottom: '0' }}
        >
          {tabs.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '10px 16px',
                border: 'none',
                backgroundColor: 'transparent',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: activeTab === id ? 600 : 400,
                color: activeTab === id ? 'var(--orange-9)' : 'var(--gray-10)',
                borderBottom: activeTab === id ? '2px solid var(--orange-9)' : '2px solid transparent',
                transition: 'all 0.15s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon style={{ width: '16px', height: '16px' }} />
              {label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div
          className="rounded-xl p-6 md:p-8"
          style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
        >
          {/* Saved Methods */}
          {activeTab === 'saved' && (
            <div>
              {methods.length === 0 ? (
                <div className="text-center py-10">
                  <div style={{
                    width: '56px', height: '56px', borderRadius: '50%',
                    backgroundColor: 'var(--orange-3)', display: 'flex',
                    alignItems: 'center', justifyContent: 'center', margin: '0 auto 12px',
                  }}>
                    <CreditCardIcon style={{ width: '26px', height: '26px', color: 'var(--orange-9)' }} />
                  </div>
                  <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 4px' }}>
                    No saved payment methods
                  </p>
                  <p style={{ fontSize: '13px', color: 'var(--gray-10)', margin: '0 0 16px' }}>
                    Add a card or mobile money to make checkout faster
                  </p>
                  <Button onClick={() => setActiveTab('add')}>Add Payment Method</Button>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                  {methods.map(m => (
                    <div
                      key={m.id}
                      className="rounded-lg p-4 flex items-center justify-between"
                      style={{ backgroundColor: 'var(--color-background)', border: '1px solid var(--gray-4)' }}
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div style={{
                          width: '44px', height: '44px', borderRadius: '10px',
                          backgroundColor: m.type === 'card' ? 'var(--orange-3)' : 'var(--green-3, #dcfce7)',
                          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          {m.type === 'card'
                            ? <CreditCardIcon style={{ width: '22px', height: '22px', color: 'var(--orange-9)' }} />
                            : <DevicePhoneMobileIcon style={{ width: '22px', height: '22px', color: '#22c55e' }} />
                          }
                        </div>
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>
                              {m.type === 'card' ? `${m.brand} •••• ${m.last4}` : `${m.provider} •••• ${m.last4}`}
                            </p>
                            {m.isDefault && (
                              <span
                                style={{
                                  display: 'inline-flex', alignItems: 'center', gap: '3px',
                                  padding: '2px 8px', borderRadius: '999px', fontSize: '11px', fontWeight: 600,
                                  backgroundColor: 'var(--orange-3)', color: 'var(--orange-11)',
                                }}
                              >
                                <CheckBadgeIcon style={{ width: '12px', height: '12px' }} /> Default
                              </span>
                            )}
                          </div>
                          <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: '2px 0 0', textAlign: 'left' }}>
                            {m.nickname && <span>{m.nickname} · </span>}
                            {m.holderName}
                            {m.type === 'card' && <span> · Expires {m.expiry}</span>}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!m.isDefault && (
                          <button
                            type="button"
                            onClick={() => handleSetDefault(m.id)}
                            title="Set as default"
                            style={{
                              padding: '6px', borderRadius: '8px', border: '1px solid var(--gray-5)',
                              backgroundColor: 'transparent', cursor: 'pointer', display: 'flex',
                            }}
                          >
                            <StarIcon style={{ width: '16px', height: '16px', color: 'var(--gray-10)' }} />
                          </button>
                        )}
                        {m.isDefault && (
                          <span
                            title="Default"
                            style={{
                              padding: '6px', borderRadius: '8px', border: '1px solid var(--orange-5)',
                              backgroundColor: 'var(--orange-2)', display: 'flex',
                            }}
                          >
                            <StarSolid style={{ width: '16px', height: '16px', color: 'var(--orange-9)' }} />
                          </span>
                        )}
                        <button
                          type="button"
                          onClick={() => handleDelete(m.id)}
                          title="Remove"
                          style={{
                            padding: '6px', borderRadius: '8px', border: '1px solid var(--red-6, #fca5a5)',
                            backgroundColor: 'transparent', cursor: 'pointer', display: 'flex',
                          }}
                        >
                          <TrashIcon style={{ width: '16px', height: '16px', color: 'var(--red-9, #e5484d)' }} />
                        </button>
                      </div>
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={() => setActiveTab('add')}
                    className="rounded-lg flex items-center justify-center gap-2 mt-2"
                    style={{
                      padding: '12px', border: '1px dashed var(--orange-6)',
                      backgroundColor: 'transparent', color: 'var(--orange-9)',
                      fontSize: '13px', fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    <PlusIcon style={{ width: '16px', height: '16px' }} />
                    Add another method
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Add New */}
          {activeTab === 'add' && (
            <div className="space-y-5">
              {/* Type selector */}
              <div className="grid grid-cols-2 gap-3">
                {[
                  { id: 'card', label: 'Card', icon: CreditCardIcon, sub: 'Visa, Mastercard, Amex' },
                  { id: 'mobile', label: 'Mobile Money', icon: DevicePhoneMobileIcon, sub: 'MTN, Airtel' },
                ].map(({ id, label, icon: Icon, sub }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setMethodType(id)}
                    className="rounded-lg p-4 text-left flex items-center gap-3"
                    style={{
                      border: `1px solid ${methodType === id ? 'var(--orange-9)' : 'var(--gray-4)'}`,
                      backgroundColor: methodType === id ? 'var(--orange-2)' : 'var(--color-background)',
                      cursor: 'pointer',
                    }}
                  >
                    <Icon style={{ width: '22px', height: '22px', color: methodType === id ? 'var(--orange-9)' : 'var(--gray-10)' }} />
                    <div>
                      <p style={{ fontSize: '14px', fontWeight: 600, color: 'var(--gray-12)', margin: 0, textAlign: 'left' }}>{label}</p>
                      <p style={{ fontSize: '12px', color: 'var(--gray-10)', margin: 0, textAlign: 'left' }}>{sub}</p>
                    </div>
                  </button>
                ))}
              </div>

              {/* Card Form */}
              {methodType === 'card' && (
                <form onSubmit={handleAddCard} noValidate className="space-y-4">
                  <Input
                    label="Card Number"
                    name="cardNumber"
                    value={cardForm.cardNumber}
                    onChange={(e) => updateCardField('cardNumber', formatCardNumber(e.target.value))}
                    placeholder="1234 5678 9012 3456"
                    error={cardErrors.cardNumber}
                    required
                  />
                  <Input
                    label="Cardholder Name"
                    name="holderName"
                    value={cardForm.holderName}
                    onChange={(e) => updateCardField('holderName', e.target.value)}
                    placeholder="As shown on card"
                    error={cardErrors.holderName}
                    required
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <Input
                      label="Expiry (MM/YY)"
                      name="expiry"
                      value={cardForm.expiry}
                      onChange={(e) => updateCardField('expiry', formatExpiry(e.target.value))}
                      placeholder="MM/YY"
                      error={cardErrors.expiry}
                      required
                    />
                    <Input
                      label="CVV"
                      type="password"
                      name="cvv"
                      value={cardForm.cvv}
                      onChange={(e) => updateCardField('cvv', e.target.value.replace(/\D/g, '').slice(0, 4))}
                      placeholder="123"
                      error={cardErrors.cvv}
                      required
                    />
                  </div>
                  <Input
                    label="Nickname (optional)"
                    name="nickname"
                    value={cardForm.nickname}
                    onChange={(e) => updateCardField('nickname', e.target.value)}
                    placeholder="e.g. Personal Visa"
                  />

                  <div className="flex justify-end">
                    <Button type="submit" loading={adding}>Add Card</Button>
                  </div>
                </form>
              )}

              {/* Mobile Money Form */}
              {methodType === 'mobile' && (
                <form onSubmit={handleAddMobile} noValidate className="space-y-4">
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '6px', textAlign: 'left' }}>
                      Provider
                    </label>
                    <select
                      value={mobileForm.provider}
                      onChange={(e) => setMobileForm(f => ({ ...f, provider: e.target.value }))}
                      style={{
                        width: '100%', padding: '8px 12px', borderRadius: '8px',
                        border: '1px solid var(--gray-5)', backgroundColor: 'var(--color-background)',
                        color: 'var(--gray-12)', fontSize: '14px', outline: 'none',
                      }}
                    >
                      <option value="MTN">MTN Mobile Money</option>
                      <option value="Airtel">Airtel Money</option>
                      <option value="M-Pesa">M-Pesa</option>
                    </select>
                  </div>
                  <Input
                    label="Phone Number"
                    name="phone"
                    value={mobileForm.phone}
                    onChange={(e) => updateMobileField('phone', e.target.value)}
                    placeholder="+256 7XX XXX XXX"
                    error={mobileErrors.phone}
                    required
                  />
                  <Input
                    label="Account Holder Name"
                    name="holderName"
                    value={mobileForm.holderName}
                    onChange={(e) => updateMobileField('holderName', e.target.value)}
                    placeholder="Registered account name"
                    error={mobileErrors.holderName}
                    required
                  />
                  <Input
                    label="Nickname (optional)"
                    name="nickname"
                    value={mobileForm.nickname}
                    onChange={(e) => updateMobileField('nickname', e.target.value)}
                    placeholder="e.g. My MTN line"
                  />

                  <div className="flex justify-end">
                    <Button type="submit" loading={adding}>Add Mobile Money</Button>
                  </div>
                </form>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;
